import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import CalendarHeatmap from "react-calendar-heatmap";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
} from "recharts";
import PracticeSessionStatsService from "@/services/PracticeSessionStatService";
import PageContainer from "@/components/PageContainer";
import InnerContainerLoader from "@/components/InnerContainerLoader";

import "./stats.css";
import "react-calendar-heatmap/dist/styles.css";
import "react-tooltip/dist/react-tooltip.css";
import { THEME, accuracyToColor } from "@/lib/theme";
import { Gauge, MessageSquareText, Timer } from "lucide-react";
import PriorityBadge from "@/components/LearningSession/PriorityBadge";
import { combinePhonemeEntries, normalizePhonemeEntries } from "@/utils/phonemeUtils";

const RESOLUTION_CONFIG = {
  "7_days": { label: "7 DAYS", days: 7 },
  "30_days": { label: "30 DAYS", days: 30 },
  "1_year": { label: "1 YEAR", days: 365 },
};

const TARGET_SPEAKING_RATE = 150;

const normalizeSession = (session) => {
  const timestampValue =
    session?.timestamp ||
    session?.created_at ||
    session?.createdAt ||
    session?.updated_at;
  const timestamp = new Date(timestampValue);
  if (Number.isNaN(timestamp.getTime())) {
    return null;
  }
  const duration =
    Number.parseFloat(session?.duration ?? session?.total_duration ?? 0) || 0;
  const totalWords =
    Number.parseFloat(session?.total_words ?? session?.totalWords ?? 0) || 0;
  const wordsPerMinute =
    Number.parseFloat(
      session?.words_per_minute ?? session?.wordsPerMinute ?? 0
    ) || 0;
  const accuracy =
    Number.parseFloat(session?.accuracy ?? session?.avg_accuracy ?? 0) || 0;
  const language = session?.language || "Unknown";

  return {
    language,
    duration,
    totalWords,
    wordsPerMinute,
    accuracy,
    timestamp,
    raw: session,
  };
};

const formatDuration = (seconds) => {
  if (seconds <= 0) {
    return "0s";
  }
  const totalSeconds = Math.round(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${secs}s`;
};

const computePercentDelta = (currentValue, previousValue) => {
  if (previousValue <= 0) {
    if (currentValue > 0) {
      return { label: "New high vs prior period", tone: "up" };
    }
    return { label: "No change vs prior period", tone: "flat" };
  }
  const delta = ((currentValue - previousValue) / previousValue) * 100;
  if (delta === 0) {
    return { label: "On pace with prior period", tone: "flat" };
  }
  const direction = delta > 0 ? "up" : "down";
  return {
    label: `${delta > 0 ? "+" : "-"}${Math.abs(delta).toFixed(
      0
    )}% vs prior period`,
    tone: direction,
  };
};

const computeAbsoluteDelta = (currentValue, previousValue, unit) => {
  const diff = currentValue - previousValue;
  if (diff === 0) {
    return { label: `No change vs prior period`, tone: "flat" };
  }
  const direction = diff > 0 ? "up" : "down";
  return {
    label: `${diff > 0 ? "+" : "-"}${Math.abs(diff)} ${unit} vs prior period`,
    tone: direction,
  };
};

const aggregateSessions = (sessions) => {
  return sessions.reduce(
    (acc, session) => {
      acc.totalDuration += session.duration;
      acc.totalWords += session.totalWords;
      const weight = session.totalWords > 0 ? session.totalWords : 1;
      if (session.wordsPerMinute > 0) {
        acc.weightedWpm += session.wordsPerMinute * weight;
        acc.wpmWeight += weight;
      }
      if (session.accuracy > 0) {
        acc.weightedAccuracy += session.accuracy;
        acc.accuracySamples += 1;
      }
      acc.sessionCount += 1;
      return acc;
    },
    {
      totalDuration: 0,
      totalWords: 0,
      weightedWpm: 0,
      wpmWeight: 0,
      weightedAccuracy: 0,
      accuracySamples: 0,
      sessionCount: 0,
    }
  );
};

// Sessions scored by the old Modal model stored sounds character by
// character ("a", "ʊ", "ː"); LangCraft sessions store whole sounds ("aʊ") and
// carry `sound_risks`. Rebuild old entries so both count under the same sound.
const SOUND_ALIASES = { "ɔø": "ɔʏ" };

const soundEntriesForWord = (word) => {
  const matches = Array.isArray(word?.phoneme_matches) ? word.phoneme_matches : [];
  const entries = Array.isArray(word?.sound_risks)
    ? matches.map((match) => {
        const [symbol, status] =
          match && typeof match === "object" ? Object.entries(match)[0] || [] : [];
        return { symbol, status };
      })
    : combinePhonemeEntries(normalizePhonemeEntries(matches));
  return entries.map(({ symbol, status }) => ({
    symbol: SOUND_ALIASES[symbol] || symbol,
    status,
  }));
};

// Functional load across sessions: words where a slip could make them sound
// like another word, most frequent first. Only LangCraft-scored sessions
// carry this data.
const aggregateMeaningRisks = (sessions) => {
  const byWord = new Map();
  let trackedSessions = 0;
  sessions.forEach((session) => {
    const words = Array.isArray(session.raw?.transcribed_words)
      ? session.raw.transcribed_words
      : [];
    if (words.some((word) => Array.isArray(word?.sound_risks))) {
      trackedSessions += 1;
    }
    words.forEach((word) => {
      const risks = (word?.sound_risks || [])
        .map((risk, index) => risk && { risk, index })
        .filter(Boolean);
      if (risks.length === 0) return;

      const text = (word.text || "")
        .replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "")
        .toLowerCase();
      // Sessions are newest first, so the first hit is the latest attempt.
      const entry = byWord.get(text) || { word: text, count: 0, slips: new Map(), score: 0 };
      entry.count += 1;
      risks.forEach(({ risk, index }) => {
        const [target] = Object.keys(word.phoneme_matches?.[index] || {});
        const [said] = Object.keys(word.transcribed_phonemes?.[index] || {});
        if (target && !entry.slips.has(target)) entry.slips.set(target, said);
        if ((risk.score || 0) > entry.score) {
          Object.assign(entry, {
            score: risk.score || 0,
            priority: risk.priority,
            soundsLike: risk.sounds_like,
          });
        }
      });
      byWord.set(text, entry);
    });
  });
  const items = Array.from(byWord.values())
    .map((entry) => ({
      ...entry,
      slips: Array.from(entry.slips, ([target, said]) => ({ target, said })),
    }))
    .sort((a, b) => b.count - a.count || b.score - a.score);
  return { items, trackedSessions };
};

// Dark, themed tooltip for the words-spoken chart.
const WordsTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const words = payload[0].value || 0;
  return (
    <div className="rounded-md border border-line bg-elevated px-3 py-2 shadow-panel">
      <div className="text-xs text-fg-muted">{label}</div>
      <div className="mt-0.5 font-display text-base font-semibold text-fg">
        {words > 0 ? (
          <>
            <span className="text-primary">{words}</span> {words === 1 ? "word" : "words"}
          </>
        ) : (
          <span className="text-fg-subtle">No practice</span>
        )}
      </div>
    </div>
  );
};

const aggregatePhonemeStats = (sessions) => {
  const phonemeMap = new Map();
  let totalWords = 0;

  sessions.forEach((session) => {
    const transcribedWords = Array.isArray(session.raw?.transcribed_words)
      ? session.raw.transcribed_words
      : [];

    totalWords += transcribedWords.length;

    transcribedWords.forEach((word) => {
      soundEntriesForWord(word).forEach(({ symbol: rawPhoneme, status }) => {
        const phoneme = (rawPhoneme || "").trim();
        if (!phoneme || phoneme.toLowerCase() === "missing") {
          return;
        }
        const normalizedStatus = String(status || "").toUpperCase();
        const existing = phonemeMap.get(phoneme) || {
          phoneme,
          correct: 0,
          total: 0,
        };
        existing.total += 1;
        if (normalizedStatus === "M") {
          existing.correct += 1;
        }
        phonemeMap.set(phoneme, existing);
      });
    });
  });

  return {
    phonemeMap,
    totalWords,
    sessionCount: sessions.length,
  };
};

const mapPhonemeStatsToItems = (phonemeMap) =>
  Array.from(phonemeMap.values())
    .map(({ phoneme, correct, total }) => {
      const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;
      return {
        phoneme,
        accuracy,
        total,
        progressWidth: `${Math.min(Math.max(accuracy, 0), 100)}%`,
      };
    })
    // Weakest sounds first, so the list doubles as a practice to-do list.
    .sort(
      (a, b) =>
        a.accuracy - b.accuracy ||
        b.total - a.total ||
        a.phoneme.localeCompare(b.phoneme)
    );

const Stats = () => {
  const [resolution, setResolution] = useState("7_days");
  const [sessionHistory, setSessionHistory] = useState([]);
  const [sessionHistoryLoading, setSessionHistoryLoading] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);

  useEffect(() => {
    const fetchSessionHistory = async () => {
      setSessionHistoryLoading(true);
      try {
        const response =
          await PracticeSessionStatsService.getUserPracticeSessionStats();
        const normalized = (Array.isArray(response) ? response : [])
          .map(normalizeSession)
          .filter(Boolean)
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        setSessionHistory(normalized);
      } catch (error) {
        console.error("Error fetching practice session history:", error);
        setSessionHistory([]);
      } finally {
        setSessionHistoryLoading(false);
      }
    };

    fetchSessionHistory();
  }, []);

  const filteredSessions = sessionHistory;

  const periodConfig = RESOLUTION_CONFIG[resolution];
  const periodDays = periodConfig.days;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const periodStart = new Date(today);
  periodStart.setDate(periodStart.getDate() - (periodDays - 1));

  const periodEnd = new Date(today);
  periodEnd.setHours(23, 59, 59, 999);

  const previousPeriodEnd = new Date(periodStart);
  previousPeriodEnd.setMilliseconds(-1);
  const previousPeriodStart = new Date(previousPeriodEnd);
  previousPeriodStart.setDate(previousPeriodStart.getDate() - (periodDays - 1));
  previousPeriodStart.setHours(0, 0, 0, 0);
  const currentYear = today.getFullYear();
  const heatmapStart = new Date(currentYear, 0, 1);
  heatmapStart.setHours(0, 0, 0, 0);
  const heatmapEnd = new Date(currentYear, 11, 31);
  heatmapEnd.setHours(23, 59, 59, 999);

  const isWithinRange = (timestamp, start, end) =>
    timestamp.getTime() >= start.getTime() &&
    timestamp.getTime() <= end.getTime();

  const currentPeriodSessions = filteredSessions.filter((session) =>
    isWithinRange(session.timestamp, periodStart, periodEnd)
  );

  const previousPeriodSessions = filteredSessions.filter((session) =>
    isWithinRange(session.timestamp, previousPeriodStart, previousPeriodEnd)
  );

  const currentAgg = aggregateSessions(currentPeriodSessions);
  const previousAgg = aggregateSessions(previousPeriodSessions);

  const totalDurationDelta = computePercentDelta(
    currentAgg.totalDuration,
    previousAgg.totalDuration
  );
  const totalWordsDelta = computeAbsoluteDelta(
    currentAgg.totalWords,
    previousAgg.totalWords,
    "words"
  );

  const averageSpeakingRate =
    currentAgg.wpmWeight > 0 ? currentAgg.weightedWpm / currentAgg.wpmWeight : 0;
  const speakingRateGap = TARGET_SPEAKING_RATE - averageSpeakingRate;
  const speakingTone =
    averageSpeakingRate >= TARGET_SPEAKING_RATE
      ? { label: "Above target pace", tone: "up" }
      : {
          label: `Target: ${TARGET_SPEAKING_RATE} wpm`,
          tone: "down",
        };

  const currentPhonemeStats = useMemo(
    () => aggregatePhonemeStats(currentPeriodSessions),
    [currentPeriodSessions]
  );
  const meaningRisks = useMemo(
    () => aggregateMeaningRisks(currentPeriodSessions),
    [currentPeriodSessions]
  );
  const previousPhonemeStats = useMemo(
    () => aggregatePhonemeStats(previousPeriodSessions),
    [previousPeriodSessions]
  );

  const currentFluencyAccuracy =
    currentAgg.accuracySamples > 0
      ? currentAgg.weightedAccuracy / currentAgg.accuracySamples
      : 0;
  const previousFluencyAccuracy =
    previousAgg.accuracySamples > 0
      ? previousAgg.weightedAccuracy / previousAgg.accuracySamples
      : 0;
  const fluencyPercent = Math.max(
    0,
    Math.min(100, currentFluencyAccuracy)
  );
  const fluencyDelta = computePercentDelta(
    currentFluencyAccuracy,
    previousFluencyAccuracy
  );

  const practiceStreak = useMemo(() => {
    const dayHasPractice = new Set();
    filteredSessions.forEach((session) => {
      if (session.totalWords > 0 || session.duration > 0) {
        const key = format(session.timestamp, "yyyy-MM-dd");
        dayHasPractice.add(key);
      }
    });

    let streak = 0;
    const checkDate = new Date(today);
    for (let index = 0; index < 30; index += 1) {
      const key = format(checkDate, "yyyy-MM-dd");
      if (dayHasPractice.has(key)) {
        streak += 1;
      } else {
        break;
      }
      checkDate.setDate(checkDate.getDate() - 1);
    }

    const currentDayIndex = today.getDay();
    const monday = new Date(today);
    const offset = (currentDayIndex + 6) % 7;
    monday.setDate(monday.getDate() - offset);

    const week = Array.from({ length: 7 }, (_, idx) => {
      const current = new Date(monday);
      current.setDate(monday.getDate() + idx);
      const key = format(current, "yyyy-MM-dd");
      const practiced = dayHasPractice.has(key);
      return {
        label: format(current, "EEE"),
        practiced,
        dateLabel: format(current, "dd"),
      };
    });

    return { streak, week };
  }, [filteredSessions, today]);

  const aiInsight = useMemo(() => {
    if (!filteredSessions.length) {
      return {
        title: "Warm up when you’re ready",
        body:
          "Kick off a fresh session to unlock tailored feedback. We’ll surface your progress and coach you toward the next milestone right here.",
      };
    }

    const latestSession = filteredSessions[0];
    const durationText = formatDuration(latestSession.duration);
    const wordsText =
      latestSession.totalWords > 0
        ? `${latestSession.totalWords} ${latestSession.totalWords === 1 ? "word" : "words"}`
        : "a few words";
    const accuracyText =
      latestSession.accuracy > 0
        ? `${Math.round(latestSession.accuracy)}% accuracy`
        : "accuracy data pending";
    const wpmText =
      latestSession.wordsPerMinute > 0
        ? `${Math.round(latestSession.wordsPerMinute)} wpm`
        : "pace warming up";

    const accuracyFor = (stat) =>
      stat && stat.total > 0 ? (stat.correct / stat.total) * 100 : 0;

    const improvementCandidates = [];
    currentPhonemeStats.phonemeMap.forEach((stat, phoneme) => {
      const previous = previousPhonemeStats.phonemeMap.get(phoneme);
      if (!previous || previous.total <= 0) {
        return;
      }
      const delta = accuracyFor(stat) - accuracyFor(previous);
      if (Math.abs(delta) >= 0.5) {
        improvementCandidates.push({ phoneme, delta });
      }
    });

    const positiveImprovements = improvementCandidates
      .filter(({ delta }) => delta > 0)
      .sort((a, b) => b.delta - a.delta)
      .slice(0, 2);

    const improvementText = positiveImprovements.length
      ? `Your phoneme accuracy improved by ${positiveImprovements
          .map(
            ({ phoneme, delta }) =>
              `${delta.toFixed(1)}% on /${phoneme}/`
          )
          .join(" and ")}.`
      : "";

    const focusCandidate = Array.from(currentPhonemeStats.phonemeMap.values())
      .map((stat) => ({
        phoneme: stat.phoneme,
        accuracy: accuracyFor(stat),
        total: stat.total,
      }))
      .filter((entry) => entry.total >= 4)
      .sort((a, b) => a.accuracy - b.accuracy)[0];

    const focusText = focusCandidate
      ? `Focus on the /${focusCandidate.phoneme}/ sound for faster gains next week.`
      : "";

    const insightsText = [improvementText, focusText]
      .filter(Boolean)
      .join(" ");

    return {
      title: latestSession.accuracy >= 92 ? "Great progress this week!" : "Momentum is building!",
      body: `Latest session ran ${durationText} with ${wordsText} spoken at ${wpmText}. ${accuracyText}. ${
        insightsText ||
        "Keep tracking those phonemes—additional practice will surface more targeted sound insights."
      }`,
    };
  }, [filteredSessions, currentPhonemeStats, previousPhonemeStats]);

  const phonemeMastery = useMemo(() => {
    if (currentPhonemeStats.sessionCount === 0) {
      return { items: [], totalWords: 0, sessionCount: 0 };
    }

    const items = mapPhonemeStatsToItems(currentPhonemeStats.phonemeMap).slice(
      0,
      12
    );

    return {
      items,
      totalWords: currentPhonemeStats.totalWords,
      sessionCount: currentPhonemeStats.sessionCount,
    };
  }, [currentPhonemeStats]);

  const heatmapValues = useMemo(() => {
    const map = new Map();
    filteredSessions.forEach((session) => {
      if (session.timestamp.getFullYear() !== currentYear) {
        return;
      }
      const key = format(session.timestamp, "yyyy-MM-dd");
      const existing = map.get(key) || { date: key, count: 0 };
      existing.count += Math.max(1, Math.round(session.totalWords));
      map.set(key, existing);
    });
    return Array.from(map.values());
  }, [filteredSessions, currentYear]);

  const maxHeatmapCount = useMemo(() => {
    return heatmapValues.reduce(
      (max, entry) => Math.max(max, entry.count),
      0
    );
  }, [heatmapValues]);

  const wordsGraphData = useMemo(() => {
    const dataMap = new Map();
    for (let offset = 0; offset < periodDays; offset += 1) {
      const day = new Date(periodStart);
      day.setDate(periodStart.getDate() + offset);
      const key = format(day, "yyyy-MM-dd");
      dataMap.set(key, { date: key, words: 0 });
    }

    currentPeriodSessions.forEach((session) => {
      const key = format(session.timestamp, "yyyy-MM-dd");
      if (!dataMap.has(key)) {
        dataMap.set(key, { date: key, words: 0 });
      }
      dataMap.get(key).words += session.totalWords;
    });

    return Array.from(dataMap.values()).map((entry) => ({
      ...entry,
      label: format(new Date(entry.date), periodDays > 30 ? "MMM" : "MMM d"),
    }));
  }, [currentPeriodSessions, periodDays, periodStart]);

  if (sessionHistoryLoading) {
    return (
      <PageContainer innerClassName="items-center justify-center">
        <InnerContainerLoader offset={0} fullscreen />
      </PageContainer>
    );
  }

  const totalPracticeValue = formatDuration(currentAgg.totalDuration);
  const totalWordsValue = new Intl.NumberFormat().format(
    Math.round(currentAgg.totalWords)
  );
  const speakingRateValue = Math.round(averageSpeakingRate);

  const scoreCards = [
    {
      key: "fluency",
      type: "gauge",
      title: "Overall score",
      value: `${Math.round(fluencyPercent)}%`,
      percent: fluencyPercent,
      delta: fluencyDelta,
    },
    {
      key: "practice",
      type: "standard",
      icon: Timer,
      title: "Practice time",
      value: totalPracticeValue,
      delta: totalDurationDelta,
    },
    {
      key: "words",
      type: "standard",
      icon: MessageSquareText,
      title: "Words spoken",
      value: totalWordsValue,
      delta: totalWordsDelta,
    },
    {
      key: "speaking",
      type: "standard",
      icon: Gauge,
      title: "Speaking rate",
      value: `${speakingRateValue} wpm`,
      delta:
        speakingRateGap < 0
          ? { label: "Above target pace", tone: "up" }
          : speakingTone,
    },
  ];

  return (
    <PageContainer outerClassName="scrollbar-hide" innerClassName="gap-6">
      <section className="rounded-[28px] bg-transparent p-0 text-fg">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-[3.6rem] w-[3.6rem] items-center justify-center text-[1.8rem]">
              🔥
            </span>
            <div>
              <div className="text-sm font-semibold uppercase tracking-[0.3em] text-primary/80">
                Practice Streak
              </div>
              <div className="mt-1 text-3xl font-semibold text-fg">
                {practiceStreak.streak}{" "}
                {practiceStreak.streak === 1 ? "Day" : "Days"}
              </div>
            </div>
          </div>
          <div className="grid w-full max-w-md grid-cols-7 gap-1.5 sm:flex sm:w-auto sm:gap-3">
            {practiceStreak.week.map((day) => (
              <div
                key={day.label}
                className="flex flex-col items-center gap-1"
              >
                <span className="text-[10px] font-semibold uppercase tracking-wider text-fg-muted sm:text-xs sm:tracking-[0.2em]">
                  {day.label}
                </span>
                <span
                  className={`flex aspect-square w-full max-w-[40px] items-center justify-center rounded-full border text-sm font-semibold transition sm:h-10 sm:w-10 ${
                    day.practiced
                      ? "border-primary bg-primary/15 text-primary shadow-glow-sm"
                      : "border-line bg-transparent text-fg-muted"
                  }`}
                >
                  {day.practiced ? "🔥" : day.dateLabel}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowHeatmap((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-full bg-transparent px-4 py-1.5 text-xs font-semibold text-fg-muted transition hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
          >
            <span>{showHeatmap ? "Hide heatmap" : "Heatmap"}</span>
            <svg
              aria-hidden="true"
              className={`h-3 w-3 text-primary/80 transition-transform ${
                showHeatmap ? "-rotate-180" : "rotate-0"
              }`}
              viewBox="0 0 10 6"
              fill="none"
            >
              <path
                d="M1 1l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </section>

      {showHeatmap && (
        <section className="rounded-[28px] bg-transparent p-6">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.35em] text-fg-subtle">
            {currentYear}
          </div>
          <div className="overflow-x-auto">
            <CalendarHeatmap
              startDate={heatmapStart}
              endDate={heatmapEnd}
              values={heatmapValues}
              classForValue={(value) => {
                if (!value || !value.count || maxHeatmapCount <= 0) {
                  return "color-empty";
                }
                return "color-filled";
              }}
              tooltipDataAttrs={(value) => ({
                "data-tooltip-id": "practice-heatmap-tooltip",
                "data-tooltip-content": value.date
                  ? `${format(new Date(value.date), "MMM d")}: ${
                      value.count
                    } word${value.count === 1 ? "" : "s"}`
                  : "No practice",
              })}
              showWeekdayLabels={false}
            />
          </div>
          <ReactTooltip id="practice-heatmap-tooltip" />
        </section>
      )}

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="text-base font-semibold text-fg-muted">
            Overview
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <div className="flex gap-2 rounded-full border border-line bg-surface/80 p-1">
              {Object.entries(RESOLUTION_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setResolution(key)}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide transition ${
                    resolution === key
                      ? "bg-primary/80 text-primary-fg"
                      : "text-fg-muted hover:text-fg"
                  }`}
                >
                  {config.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {scoreCards.map((card) => {
            const deltaIcon =
              card.delta?.tone === "up" ? "↑" : card.delta?.tone === "down" ? "↓" : "→";
            // Down isn't an error, so it stays neutral rather than red.
            const deltaToneClass =
              card.delta?.tone === "up" ? "text-primary" : "text-fg-muted";
            if (card.type === "gauge") {
              const percentValue = Math.max(0, Math.min(100, card.percent || 0));
              const color = accuracyToColor(percentValue);
              return (
                <div
                  key={card.key}
                  className="col-span-2 flex items-center gap-4 rounded-xl border border-line bg-surface p-5 lg:col-span-1"
                >
                  <div
                    className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full"
                    style={{
                      background: `conic-gradient(${color} ${percentValue * 3.6}deg, ${THEME.line} 0deg)`,
                      boxShadow: `0 0 28px -10px ${color}`,
                    }}
                  >
                    <div className="flex h-[68px] w-[68px] items-center justify-center rounded-full bg-surface">
                      <span className="font-display text-2xl font-bold" style={{ color }}>
                        {Math.round(percentValue)}
                      </span>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-fg">{card.title}</div>
                    <div className="mt-0.5 text-xs text-fg-subtle">Pronunciation accuracy</div>
                    {card.delta && (
                      <div className={`mt-2 text-xs font-medium ${deltaToneClass}`}>
                        {deltaIcon} {card.delta.label}
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            const Icon = card.icon;
            return (
              <div
                key={card.key}
                className={`flex flex-col rounded-xl border border-line bg-surface p-4 sm:p-5 ${
                  card.key === "speaking" ? "col-span-2 lg:col-span-1" : ""
                }`}
              >
                <div className="flex items-center gap-2 text-sm text-fg-muted">
                  <Icon size={16} strokeWidth={1.8} className="text-primary" />
                  {card.title}
                </div>
                <div className="mt-3 font-display text-2xl font-bold text-fg sm:text-3xl">
                  {card.value}
                </div>
                <div className={`mt-auto pt-2 text-xs font-medium ${deltaToneClass}`}>
                  {deltaIcon} {card.delta.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <section className="grid gap-4 lg:grid-cols-[0.75fr,1.25fr]">
        <div className="panel rounded-lg border-l-2 border-l-accent p-6 text-fg">
          <div className="hud-label inline-flex items-center gap-2 rounded-sm border border-accent/40 bg-accent/10 px-3 py-1 !text-accent">
            ✨ AI Insights
          </div>
          <h2 className="mt-4 text-2xl font-semibold sm:text-3xl">
            {aiInsight.title}
          </h2>
          <p className="mt-3 max-w-md text-base leading-relaxed text-fg-muted">
            {aiInsight.body}
          </p>
        </div>

        <div className="rounded-xl border border-line bg-surface p-4 sm:p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-1">
            <div className="text-base font-semibold text-fg">Words spoken</div>
            <span className="text-xs text-fg-muted">
              {format(periodStart, "MMM d, yyyy")} –{" "}
              {format(periodEnd, "MMM d, yyyy")}
            </span>
          </div>
          <div className="h-[220px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={wordsGraphData}>
                <XAxis
                  dataKey="label"
                  stroke={THEME.fgMuted}
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  stroke={THEME.fgMuted}
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => `${value}`}
                  allowDecimals={false}
                />
                <RechartsTooltip
                  cursor={{ fill: "rgb(57 255 136 / 0.06)", radius: 4 }}
                  content={<WordsTooltip />}
                />
                <Bar dataKey="words" fill={THEME.primary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="rounded-[28px] bg-transparent py-6 sm:p-6">
        <div className="flex flex-col gap-2">
          <h3 className="text-xl font-semibold text-fg">Your sounds</h3>
          <p className="text-sm text-fg-muted">
            {phonemeMastery.items.length > 0
              ? `Weakest first · based on ${new Intl.NumberFormat().format(
                  phonemeMastery.totalWords
                )} words across ${phonemeMastery.sessionCount} ${
                  phonemeMastery.sessionCount === 1 ? "session" : "sessions"
                }`
              : "Practice sessions with phoneme feedback will populate this breakdown so you can target tricky sounds."}
          </p>
        </div>
        {phonemeMastery.items.length > 0 ? (
          <div className="mt-5 grid grid-cols-2 gap-2.5 sm:gap-3 md:grid-cols-4 lg:grid-cols-5">
            {phonemeMastery.items.map(
              ({ phoneme, accuracy, progressWidth, total }) => (
                <div
                  key={phoneme}
                  className="flex flex-col gap-2 rounded-lg border border-line bg-surface p-4 text-fg"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-mono text-xl text-fg">/{phoneme}/</span>
                    <span
                      className="font-display text-lg font-bold"
                      style={{ color: accuracyToColor(accuracy) }}
                    >
                      {accuracy}%
                    </span>
                  </div>
                  <div className="text-xs text-fg-subtle">
                    {new Intl.NumberFormat().format(total)} {total === 1 ? "try" : "tries"}
                  </div>
                  <div className="h-1 w-full rounded-full bg-line">
                    <div
                      className="h-full rounded-full"
                      style={{ width: progressWidth, backgroundColor: accuracyToColor(accuracy) }}
                    />
                  </div>
                </div>
              )
            )}
          </div>
        ) : (
          <div className="mt-5 rounded-2xl bg-transparent p-4 text-sm text-fg-muted">
            Recording a phoneme-focused session will unlock personalized
            mastery insights.
          </div>
        )}
      </section>
      <section className="rounded-xl border border-line bg-surface p-4 sm:p-6">
        <div className="flex flex-col gap-1">
          <h3 className="text-xl font-semibold text-fg">Slips that change meaning</h3>
          <p className="text-sm text-fg-muted">
            {meaningRisks.items.length > 0
              ? "Sounds that made a word sound like a different word, most frequent first. Fixing these makes you easiest to understand."
              : meaningRisks.trackedSessions > 0
              ? "No meaning-changing slips in this period. Nice work!"
              : "Meaning-change tracking starts with your next practice session."}
          </p>
        </div>
        {meaningRisks.items.length > 0 && (
          <div className="mt-5 divide-y divide-line">
            {meaningRisks.items.slice(0, 6).map((risk) => (
              <div
                key={risk.word}
                className="flex flex-wrap items-center gap-x-4 gap-y-1.5 py-3"
              >
                <span className="min-w-[96px] font-display text-lg font-semibold text-fg">
                  {risk.word}
                </span>
                <span className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-base">
                  {risk.slips.map((slip) => (
                    <span key={slip.target}>
                      <span className="text-fg">/{slip.target}/</span>
                      <span className="mx-1 text-fg-subtle">→</span>
                      <span className="text-accent">{slip.said ? `/${slip.said}/` : "missing"}</span>
                    </span>
                  ))}
                </span>
                {risk.soundsLike && (
                  <span className="text-sm text-fg-muted">
                    could sound like <span className="font-semibold text-fg">“{risk.soundsLike}”</span>
                  </span>
                )}
                <span className="ml-auto flex items-center gap-3">
                  <span className="text-xs text-fg-subtle">
                    {risk.count} {risk.count === 1 ? "time" : "times"}
                  </span>
                  <PriorityBadge priority={risk.priority} />
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </PageContainer>
  );
};

export default Stats;
